/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { Placement } from '../../placement';
import { Logger } from '../../../logger';
import { debugPixel, isAstAvailable } from '../../../util';
import { isRenderableBannerSize } from '../../placement/constants';
import type { SchEventDispatcher } from './sch/events';

import type { CampaignData, CampaignType, RenderedAdData } from '../../../types/index';
import type { PrebidAdUnit } from '../../../types/prebid';
import type { ApnAdObject, ApnTagObject } from '../../../types/vendor/ast';
import type { SiteConfig } from '../../config';

const logger = Logger.getInstance();
const PARTNER_MEMBER_IDS = [9700];
const IGNORED_ADOMAINS = ['google.com'];
const IGNORED_CREATIVES = [505457714, 270049957];
declare const __INTERACTION_SCRIPT__: string;
const INTERACTION_DETECTION_SCRIPT = __INTERACTION_SCRIPT__;

// Nodals first-party tags always embed a <script> element carrying a
// `data-nodals` attribute. This is the AST-side signal that our own tag was
// returned, since AST events carry no advertiser information.
// Contract: the marker must live on the <script> element itself.
const NODALS_TAG_MARKER_REGEX = /<script\b[^>]*\bdata-nodals\b/i;

// One-shot latch for the setKeywords-unavailable pixel: API availability is a
// page-level property of the loaded AST version, so one report covers every
// placement without emitting a pixel per placement per auction.
let astSetKeywordsMissingReported = false;

class AstPlacement extends Placement {
  // On AST inventory the ad server's tag layout is the sole native
  // authority (see refreshMediaCapabilities); prebid twin ad units must not
  // set the flag here.
  protected adUnitsDeclareNativeCapability: boolean = false;

  private willRenderNativeAd: boolean = false;
  private siteEventDispatcher: SchEventDispatcher | null;
  private nativeImpressionListener: ((event: any) => void) | null = null;
  // AST response uuid of the last render this placement processed, used to
  // distinguish a genuine auction-less refresh render (new ad response) from
  // a duplicate event for a render already handled (e.g. a re-dispatched
  // native impression event for the same ad). Null when the ad object carried
  // no uuid.
  private lastProcessedResponseIdentifier: string | null = null;
  // Keyword keys this placement has set on its AST tag via setKeywords and not
  // yet retired. Retirement must name exactly these keys so publisher keywords
  // are untouched. Lazily allocated and reused across auctions/renders — no
  // per-render allocation at 50-placement scale.
  private appliedKeywordKeys: Set<string> | null = null;
  // Cached apntag targetId for O(1) live-tag lookups; see getLiveTag()
  private cachedTagTargetId: string | null = null;

  constructor(
    adUnit: PrebidAdUnit,
    adUnitTransformer: (adUnitCode: string) => string,
    campaignData: CampaignData[],
    siteConfig: SiteConfig,
    siteEventDispatcher: SchEventDispatcher | null,
  ) {
    super(adUnit, adUnitTransformer, campaignData, siteConfig);
    this.siteEventDispatcher = siteEventDispatcher;
    logger.debug(`AST Placement created for ${this.adUnitCode}`);
  }

  /**
   * Publisher-set keywords from this placement's AST tag, surfaced as
   * placement.kvs.* facts by the base class (e.g. a Schibsted tag's
   * no-sno-adformat array - the slot's ad-server targeting identity).
   *
   * Always reads the live tag from apntag rather than this.slot: AST
   * replaces the tag object in apntag.requests.tags on every setKeywords
   * call, so cached references hold stale keywords.
   *
   * Filtered by mechanism, not by key name: keywords this engine applied
   * itself (tracked in appliedKeywordKeys for retirement) and the prebid
   * header-bidding namespace (HB_*) are the engine's and the auction's own
   * output, not publisher slot targeting.
   */
  protected getAdServerSlotTargeting(): Record<string, any> | null {
    if (!isAstAvailable()) {
      return null;
    }
    try {
      const tag = this.getLiveTag();
      const keywords = tag?.keywords;
      if (!keywords) {
        return null;
      }
      const applied = this.appliedKeywordKeys;
      const targeting: Record<string, any> = {};
      for (const [key, value] of Object.entries(keywords)) {
        if (applied?.has(key)) {
          continue;
        }
        if (key.toLowerCase().startsWith('hb_')) {
          continue;
        }
        targeting[key] = value;
      }
      return targeting;
    } catch (e) {
      logger.warn(`Error reading AST tag keywords for ${this.adUnitCode}: ${e}`);
      return null;
    }
  }

  /**
   * The live tag object for this placement's ad unit. The targetId (the tag's
   * key in apntag.requests.tags) is cached after the first successful scan so
   * later reads are O(1) instead of scanning every tag on the page - only the
   * lookup key is cached, never the tag object or its keywords, so keyword
   * changes are always observed. Publishers can tear a tag down and redefine
   * it under a NEW targetId (observed ids carry per-instance random
   * suffixes), so a cache hit is verified against invCode and the scan re-run
   * when it no longer matches.
   */
  private getLiveTag(): ApnTagObject | undefined {
    const apn = window.apntag;
    if (this.cachedTagTargetId) {
      const cached = apn?.getTag(this.cachedTagTargetId);
      if (cached && cached.invCode === this.adUnitCode) {
        return cached;
      }
      this.cachedTagTargetId = null;
    }
    const allTags: Record<string, ApnTagObject> = apn?.getTag() || {};
    const tag = Object.values(allTags).find(
      (candidate: ApnTagObject) => candidate.invCode === this.adUnitCode,
    );
    if (tag) {
      this.cachedTagTargetId = tag.targetId;
    }
    return tag;
  }

  protected setSlot(): void {
    if (this.slot) {
      return;
    }
    if (isAstAvailable()) {
      logger.debug(`Determining AST tag for adUnit: ${this.adUnitCode}`);
      try {
        const allTags: Record<string, ApnTagObject> = window.apntag?.getTag() || {};
        const matchingTag = Object.values(allTags).find((tag: ApnTagObject) => {
          logger.debug(
            `Checking AST tag ${tag.invCode} for invCode match against ${this.adUnitCode}`,
          );
          return tag.invCode === this.adUnitCode;
        });
        this.slot = matchingTag || null;
        if (this.slot) {
          logger.info(`AST tag located for ${this.adUnitCode}`);
        }
      } catch (e) {
        logger.error(`Error getting AST tag for ${this.adUnitCode}: ${e}`);
        this.slot = null;
      }
    } else {
      logger.error(
        `AST tag not available, unable to determine slot for adUnit: ${this.adUnitCode}`,
      );
    }
  }

  /**
   * Push key-values straight onto the AST tag via apntag.setKeywords as soon
   * as they are known (select response processed, rules run), ahead of the
   * publisher's Prebid targeting flush — mirrors the GPT implementation.
   *
   * AST specifics:
   * - setKeywords DEFAULTS TO APPEND (values accumulate across calls); every
   *   call must pass {overrideKeyValue: true} for per-key overwrite so
   *   repeated application is idempotent — including against the Prebid
   *   flush of the identical values.
   * - A null value is stored on the tag but dropped when the auction request
   *   is built (fails AST's string check), omitting the key entirely — the
   *   only clean removal. An empty string would serialise as a real empty
   *   value and an empty array still emits the key, so '' maps to null here.
   * - setKeywords is always invoked on the global apntag, never a cached tag
   *   object: AST replaces the tag object in apntag.requests.tags on every
   *   setKeywords call, so cached references (this.slot) go stale.
   *
   * Returns true when the tag-level path delivered (or had nothing to do),
   * false when it could not (no tag, no setKeywords API, call threw) and the
   * Prebid flush remains the delivery mechanism.
   */
  public applyKeyValuesToAdServer(campaignTypes: CampaignType[]): boolean {
    const keyValues = this.getKeyValuesForCampaignType(campaignTypes);
    const applied = this.appliedKeywordKeys;
    if (keyValues.length === 0 && (!applied || applied.size === 0)) {
      // Nothing to set and nothing to retire.
      return true;
    }
    this.setSlot();
    if (this.slot === null) {
      logger.debug(`No AST tag bound for ${this.adUnitCode}, key-values not applied to tag`);
      return false;
    }
    const apn = window.apntag;
    if (!apn || typeof apn.setKeywords !== 'function') {
      if (!astSetKeywordsMissingReported) {
        astSetKeywordsMissingReported = true;
        debugPixel({ en: 'astKwUnavailable', adUnitCode: this.adUnitCode });
      }
      return false;
    }
    const keywords: Record<string, string | null> = {};
    keyValues.forEach((kv) => {
      keywords[kv.key] = kv.value === '' ? null : kv.value;
    });
    // Retire keys applied in an earlier auction that this selection no longer
    // produces (select returned nothing for them, no intervening render).
    applied?.forEach((key) => {
      if (!(key in keywords)) {
        keywords[key] = null;
      }
    });
    const targetId = (this.slot as ApnTagObject).targetId;
    try {
      apn.setKeywords(targetId, keywords, { overrideKeyValue: true });
    } catch (e) {
      logger.error(`apntag.setKeywords failed for ${this.adUnitCode}: ${e}`);
      // Tracking is deliberately unchanged: nothing was applied, and a later
      // successful apply must still retire whatever the tag really holds.
      return false;
    }
    const tracked = this.appliedKeywordKeys ?? (this.appliedKeywordKeys = new Set<string>());
    tracked.clear();
    keyValues.forEach((kv) => {
      if (kv.value !== '') {
        tracked.add(kv.key);
      }
    });
    this.markKeyValuesDelivered();
    logger.info(`Applied ${tracked.size} keywords to AST tag for ${this.adUnitCode}`);
    return true;
  }

  protected retireAppliedAdServerTargeting(): void {
    this.retireAppliedKeywords();
  }

  // Null out exactly the keywords this placement applied ({key: null} under
  // overrideKeyValue — AST omits null-valued keys from the auction request).
  // Fires when a request has consumed the keywords: they must not ride any
  // later ad request without a fresh selection.
  private retireAppliedKeywords(): void {
    const applied = this.appliedKeywordKeys;
    if (!applied || applied.size === 0) {
      return;
    }
    const apn = window.apntag;
    if (this.slot !== null && apn && typeof apn.setKeywords === 'function') {
      const keywords: Record<string, null> = {};
      applied.forEach((key) => {
        keywords[key] = null;
      });
      try {
        apn.setKeywords((this.slot as ApnTagObject).targetId, keywords, {
          overrideKeyValue: true,
        });
        logger.debug(`Retired ${applied.size} AST keywords for ${this.adUnitCode}`);
      } catch (e) {
        logger.error(`Failed to retire AST keywords for ${this.adUnitCode}: ${e}`);
      }
    }
    applied.clear();
  }

  protected setSupportedSizes(): void {
    try {
      const apnTag = this.slot as ApnTagObject;
      if (apnTag?.sizes) {
        apnTag.sizes.forEach((size: number[]) => {
          // Every renderable size the tag advertises counts, custom
          // direct-sold sizes included — ad-level placement.sizes rules
          // imported from the ad server reference them (see
          // isRenderableBannerSize; 1x1 marks native, not a banner)
          if (isRenderableBannerSize(size?.[0], size?.[1])) {
            this.addSupportedSize({ width: size[0], height: size[1] });
          }
        });
      }
      // Native capability is refreshed per setFacts pass from the live tag
      // store (see refreshMediaCapabilities); the bind-time check here just
      // catches layouts already present when the slot binds.
      if (apnTag?.native) {
        this.supportsNative = true;
      }
    } catch (e) {
      logger.warn(`Error determining AST tag size: ${e}`);
    }
  }

  /**
   * Native capability comes from the tag's native layout and ONLY from it:
   * AST tags routinely carry a 1x1 size for tracking creatives, so 1x1
   * (unlike GPT's fluid/1x1 convention) proves nothing here — VG's front
   * netboard_2 lists 1x1 yet has no native layout and has never served a
   * native impression.
   *
   * The layout must be read from apntag.requests.tags, and re-read on every
   * facts pass: it exists ONLY on that store's entry — the slot reference
   * cached at bind time and the objects apntag.getTag() returns both lack
   * the field (verified live on vg.no, 2026-08-24) — and AST replaces store
   * entries over the tag's lifecycle. Sticky once seen: layouts are not
   * withdrawn mid-page, and flapping would re-suppress a slot mid-auction.
   */
  protected refreshMediaCapabilities(): void {
    if (this.supportsNative) {
      return;
    }
    try {
      const tags = window.apntag?.requests?.tags;
      if (!tags) {
        return;
      }
      const cachedId = this.cachedTagTargetId;
      const byTarget = cachedId ? tags[cachedId] : undefined;
      const tag =
        byTarget && byTarget.invCode === this.adUnitCode
          ? byTarget
          : Object.values(tags).find(
              (candidate: ApnTagObject) => candidate.invCode === this.adUnitCode,
            );
      if (tag?.native) {
        this.supportsNative = true;
      }
    } catch (e) {
      logger.warn(`Error reading AST tag native layout for ${this.adUnitCode}: ${e}`);
    }
  }

  protected determineVisibility(): void {
    const size = this.getSmallestSupportedSize();
    if (!size || this.slot === null) {
      return;
    }

    const apnTag = this.slot as ApnTagObject;
    const element = document.getElementById(apnTag.targetId);
    if (!element) {
      logger.info(`AST adunit '${this.adUnitCode}' target element '${apnTag.targetId}' not found`);
      return;
    }

    const rect = element.getBoundingClientRect();
    const elementArea = size.width * size.height;

    if (elementArea === 0) {
      return;
    }

    const adLeft = rect.left + (rect.width - size.width) / 2;
    const adTop = rect.top + (rect.height - size.height) / 2;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    const visibleWidth = Math.max(
      0,
      Math.min(adLeft + size.width, viewportWidth) - Math.max(adLeft, 0),
    );

    const visibleHeight = Math.max(
      0,
      Math.min(adTop + size.height, viewportHeight) - Math.max(adTop, 0),
    );

    const visibleArea = visibleWidth * visibleHeight;

    if (visibleArea >= elementArea / 2) {
      this.isVisible(true);
    } else {
      this.isVisible(false);
    }
  }

  protected setWrappingDomElement(): void {
    if (this.slot) {
      const apnTag = this.slot as ApnTagObject;
      this.wrappingDomElement = document.getElementById(apnTag.utDivId || apnTag.targetId);
    }
  }

  protected resetStateForNewOpportunity(): void {
    this.willRenderNativeAd = false;
  }

  protected addListeners(): void {
    logger.debug(`Adding AST listeners for placement: ${this.adUnitCode}`);
    this.addApnAdRequestedListener();
    this.addApnAdAvailableListener();
    this.addApnAdSlotRenderStartListener();
    this.addViewableImpressionListener();
    this.addPrebidAdRenderedListener();
    this.addNativeImpressionListener();
  }

  private addApnAdRequestedListener(): void {
    if (!this.slot) {
      return;
    }
    try {
      const apnTag = this.slot as ApnTagObject;
      logger.debug(`Registering adRequested listener on apntag for: ${this.adUnitCode}`);
      // Subscriptions survive the tag-object replacement setKeywords performs,
      // like the render listeners below.
      apnTag.on('adRequested', apnTag.targetId, () => {
        // Applied keywords ride this request: consumption/retirement happen
        // here, not at the render events (see consumeHeldKeyValues).
        this.consumeHeldKeyValues();
      });
      // Response events back up adRequested: a response proves the request
      // left, and consumption is idempotent, so an AST build that never emits
      // adRequested still cannot starve the placement in a permanent hold.
      for (const responseEvent of ['adAvailable', 'adNoBid', 'adError'] as const) {
        apnTag.on(responseEvent, apnTag.targetId, () => {
          this.consumeHeldKeyValues();
        });
      }
    } catch (e) {
      logger.error(`Error adding APN adRequested listener to ${this.adUnitCode}: ${e}`);
    }
  }

  protected addAdInteractionListener(): void {
    if (this.slot === null || this.interactionListenerAdded) {
      return;
    }
    logger.info(`Adding ad interaction listener for ${this.adUnitCode}`);
    if (this.willRenderNativeAd) {
      this.addNativePlacementInteractionListener();
    } else {
      this.addIframedAdInteractionListener();
    }
    this.interactionListenerAdded = true;
  }

  private addNativePlacementInteractionListener(): void {
    if (!this.wrappingDomElement) {
      logger.warn(
        `Unable to add native ad interaction listener for ${this.adUnitCode}: wrapping DOM element not found`,
      );
      return;
    }

    this.wrappingDomElement.addEventListener(
      'click',
      () => {
        this.dispatchAdInteraction(true);
      },
      true,
    );

    logger.debug(`Added native click listener for ${this.adUnitCode}`);
  }

  private createAndHandleRenderedAdData(
    adObj: ApnAdObject,
    isPrebidAd: boolean,
    isNodalsTag: boolean = false,
  ): void {
    this.slotRenderedForOpportunity = true;
    this.lastProcessedResponseIdentifier = adObj.uuid ? String(adObj.uuid) : null;
    const creativeId = String(adObj.creativeId);
    const renderedAdData: RenderedAdData = {
      advertiserId: '', // Not available from AST
      creativeId: creativeId,
      isProgrammaticBuy: false,
      isPrebidAd: isPrebidAd,
      isNodalsTag: isNodalsTag,
      responseIdentifier: adObj.uuid,
      size: this.renderedSlotSize,
    };
    this.handleRenderedAdData(renderedAdData);
  }

  private addApnAdAvailableListener(): void {
    if (!this.slot) {
      return;
    }
    try {
      const apnTag = this.slot as ApnTagObject;
      apnTag.on('adAvailable', apnTag.targetId, (adObj: ApnAdObject) => {
        if (this.isFirstPartyAdResponse(adObj)) {
          if (adObj.adType === 'banner' && adObj.banner && adObj.banner.content) {
            const detectionLogic =
              '<scr' + 'ipt>' + INTERACTION_DETECTION_SCRIPT + '</' + 'script>';
            if (!adObj.banner.content.includes(detectionLogic)) {
              adObj.banner.content += detectionLogic;
              logger.debug(`Appended interaction script to banner ad for ${this.adUnitCode}`);
            }
          } else if (adObj.adType === 'native' && adObj.native) {
            this.willRenderNativeAd = true;
          }
        }
      });
    } catch (e) {
      logger.error(`Error adding APN adAvailable listener to ${this.adUnitCode}: ${e}`);
    }
  }

  private addApnAdSlotRenderStartListener(): void {
    if (!this.slot) {
      return;
    }

    try {
      const apnTag = this.slot as ApnTagObject;
      logger.debug(`Registering adSlotRenderStart listener on apntag  for: ${this.adUnitCode}`);

      apnTag.on('adSlotRenderStart', apnTag.targetId, (adObj: ApnAdObject) => {
        // Any render into this slot replaces the previously rendered ad; tear
        // down its listeners, impression context and latches whether or not
        // the incoming ad is one we track (mirrors the GPT handler). Without
        // this, a tracked ad's context would survive a non-first-party render
        // and a later click on the new creative (e.g. via the persistent
        // native wrapper click listener) would be attributed to the old
        // impression. Applied keywords are deliberately NOT retired here —
        // retirement happens at the adRequested event that consumed them.
        this.resetStateForNewRender();
        // A first-party banner render arriving with the per-opportunity latch
        // already consumed is an auction-less refresh (ad-server driven render
        // with no Prebid auction). Mint a placement-local opportunity so the
        // new render's impression and interactions are counted; without one
        // the slot would go dark until the next Prebid auction. Native renders
        // are not minted here — the native impression handler below mints for
        // its own path.
        if (
          this.slotRenderedForOpportunity &&
          adObj.adType === 'banner' &&
          adObj.banner &&
          this.isFirstPartyAdResponse(adObj) &&
          !this.isDuplicateOfProcessedRender(adObj)
        ) {
          this.mintRenderOpportunity();
        }
        if (!this.slotRenderedForOpportunity && this.isFirstPartyAdResponse(adObj)) {
          logger.debug(
            `APN ad render start for: ${adObj.targetId}, handled by placement: ${this.adUnitCode}, creative: ${adObj.creativeId}`,
          );
          let isPrebidAd = false;
          if (adObj.adType === 'banner' && adObj.banner) {
            // Native rendered slot size handled in adAvailable listener
            this.renderedSlotSize = { width: adObj.banner.width, height: adObj.banner.height };
            isPrebidAd = adObj.banner.content.includes('ucTag.renderAd');
            const isNodalsTag = NODALS_TAG_MARKER_REGEX.test(adObj.banner.content);
            this.createAndHandleRenderedAdData(adObj, isPrebidAd, isNodalsTag);
          }
        }
      });
    } catch (e) {
      logger.error(`Error adding APN adSlotRenderStart listener to ${this.adUnitCode}: ${e}`);
    }
  }

  private addViewableImpressionListener(): void {
    if (this.slot === null) {
      return;
    }
    try {
      const apnTag = this.slot as ApnTagObject;
      logger.debug(`Registering impressionViewable listener on apntag for: ${this.adUnitCode}`);
      apnTag.on('mrcView', apnTag.targetId, (adObj: ApnAdObject) => {
        logger.debug(
          `impressionViewable dispatched by apntag for: ${adObj.targetId}, ${this.adUnitCode}`,
        );
        this.dispatchViewableImpression();
      });
    } catch (e) {
      logger.error(`Error adding APN viewable impression listener to ${this.adUnitCode}: ${e}`);
    }
  }

  private addNativeImpressionListener(): void {
    if (!this.siteEventDispatcher) {
      return;
    }

    this.nativeImpressionListener = (event: any) => {
      const adObj = event.data;
      const apnTag = this.slot as ApnTagObject;

      if (adObj.targetId !== apnTag.targetId) {
        return;
      }

      // A native render event arriving with the per-opportunity latch already
      // consumed is either an auction-less refresh render (new ad response —
      // mint an opportunity so it is counted) or a duplicate event for the
      // render already processed (same response uuid — leave the latch
      // consumed so the live render's state and interaction listener survive
      // untouched).
      if (
        this.slotRenderedForOpportunity &&
        adObj.native &&
        !this.isDuplicateOfProcessedRender(adObj)
      ) {
        this.mintRenderOpportunity();
      }
      if (!this.slotRenderedForOpportunity) {
        logger.info(
          `Native impression event for ad: ${this.adUnitCode}, targetId: ${adObj.targetId}`,
        );
        if (adObj.native) {
          // See adSlotRenderStart: tear down the previous render's state
          // before recording the new native render.
          this.resetStateForNewRender();
          if (adObj.native.width && adObj.native.height) {
            this.renderedSlotSize = { width: adObj.native.width, height: adObj.native.height };
          } else if (adObj.native.image && adObj.native.image.width && adObj.native.image.height) {
            this.renderedSlotSize = {
              width: adObj.native.image.width,
              height: adObj.native.image.height,
            };
          }
          this.createAndHandleRenderedAdData(adObj, false);
        }
      }
    };
    this.siteEventDispatcher.on('NativeImpression', this.nativeImpressionListener);
  }

  // AST ad responses carry a uuid unique per response, so an event with the
  // uuid of the render already processed is a duplicate dispatch for the same
  // ad (e.g. the publisher's native layer re-announcing an existing render),
  // not a new ad — a legitimate re-serve of the same creative arrives under a
  // fresh uuid and still counts. Events with no uuid cannot be deduplicated
  // and are treated as new renders.
  private isDuplicateOfProcessedRender(adObj: ApnAdObject): boolean {
    return !!adObj.uuid && String(adObj.uuid) === this.lastProcessedResponseIdentifier;
  }

  private isFirstPartyAdResponse(adObj: ApnAdObject): boolean {
    return (
      !IGNORED_ADOMAINS.includes(adObj?.adomain ?? '') &&
      !IGNORED_CREATIVES.includes(adObj.creativeId) &&
      PARTNER_MEMBER_IDS.includes(adObj.buyerMemberId)
    );
  }

  public destroy(): void {
    if (this.nativeImpressionListener && this.siteEventDispatcher) {
      this.siteEventDispatcher.off('NativeImpression', this.nativeImpressionListener);
      this.nativeImpressionListener = null;
    }
    super.destroy();
  }
}

export { AstPlacement };
